import BugsnagPerformance from '@bugsnag/browser-performance';
import Bugsnag from '@bugsnag/js';
import BugsnagPluginVue from '@bugsnag/plugin-vue';
import type { App } from 'vue';

import type { AppConfig } from '@/config/fetchEnv';
import { useUserStore } from '@/stores/user';

// Projet Bugsnag « welcome » — front de cette app.
// (Avant : la clé du projet « Auth », partagée avec af/auth.)
const BUGSNAG_API_KEY = '8254e44393b01cd2c1afb5545a8d3208';

const IGNORED_MESSAGES = [
  'Network Error',
  'Failed to fetch',
  'Load failed',
  'Connection closed',
  'Request aborted',
  'Unable to preload CSS for',
  'error loading dynamically imported module',
  'ResizeObserver loop completed with undelivered notifications',
  'ResizeObserver loop limit exceeded',
  'Non-Error promise rejection',
  'cannot have a negative time stamp'
];

function getReleaseStage(environment: string): string {
  return environment === 'production' ? 'production' : 'development';
}

function getErrorMessage(error: unknown): string {
  if (typeof error === 'string') return error;
  if (error && typeof error === 'object' && 'message' in error && typeof error.message === 'string') {
    return error.message;
  }

  return '';
}

function isBrowserNoise(error: unknown): boolean {
  const message = getErrorMessage(error);
  return IGNORED_MESSAGES.some(ignoredMessage => message.includes(ignoredMessage));
}

export default function installBugsnag(config: AppConfig) {
  return {
    install(app: App) {
      const userStore = useUserStore();

      Bugsnag.start({
        apiKey: BUGSNAG_API_KEY,
        plugins: [new BugsnagPluginVue()],
        releaseStage: getReleaseStage(config.environment),
        enabledReleaseStages: ['production'],
        autoDetectErrors: true,
        enabledBreadcrumbTypes: ['error', 'log', 'navigation', 'request'],
        onError(event) {
          if (isBrowserNoise(event.originalError)) {
            return false;
          }

          const user = userStore.user;

          if (user) {
            event.setUser(user.uuid, user.email);
          }
        }
      });

      BugsnagPerformance.start({ apiKey: BUGSNAG_API_KEY });

      const bugsnagVuePlugin = Bugsnag.getPlugin('vue');

      if (bugsnagVuePlugin) {
        app.use(bugsnagVuePlugin);
      }
    }
  };
}
